import type { QueryClient } from '@tanstack/react-query'

import type { FieldValue } from '@/lib/fields/types'
import type { FieldHistoryOperation } from '@/store/field-history.store'

type RecordLike = Record<string, unknown>

const isRecord = (value: unknown): value is RecordLike =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const normalizeDateString = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return value
  const timestamp = Date.parse(value)
  if (Number.isNaN(timestamp)) return value
  return new Date(timestamp).toISOString()
}

export const normalizeFieldHistoryValue = (value: unknown): unknown => {
  if (value === undefined || value === null || value === '') return null

  if (value instanceof Date) {
    return value.toISOString()
  }

  if (typeof value === 'string') {
    return normalizeDateString(value)
  }

  if (Array.isArray(value)) {
    return value.map(normalizeFieldHistoryValue)
  }

  if (isRecord(value)) {
    if (typeof value.id === 'string' && typeof value.type === 'string') {
      return { type: value.type, id: value.id }
    }

    if (typeof value.id === 'string' && Object.keys(value).length <= 4) {
      return { id: value.id }
    }

    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = normalizeFieldHistoryValue(value[key])
        return acc
      }, {})
  }

  return value
}

export const areFieldHistoryValuesEqual = (
  left: unknown,
  right: unknown,
): boolean =>
  JSON.stringify(normalizeFieldHistoryValue(left)) ===
  JSON.stringify(normalizeFieldHistoryValue(right))

export const extractFieldHistoryValue = (
  record: unknown,
  fieldName: string,
  fieldId?: string,
): FieldValue | undefined => {
  if (!isRecord(record)) return undefined

  const candidateKeys = [
    fieldName,
    fieldId,
    fieldName.startsWith('custom_') ? fieldName.slice('custom_'.length) : null,
    fieldId?.startsWith('custom_') ? fieldId.slice('custom_'.length) : null,
  ].filter((key): key is string => !!key)

  for (const key of candidateKeys) {
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      return record[key] as FieldValue
    }
  }

  const customFields = record.customFields
  if (isRecord(customFields)) {
    for (const key of candidateKeys) {
      const customKey = key.startsWith('custom_')
        ? key.slice('custom_'.length)
        : key
      if (Object.prototype.hasOwnProperty.call(customFields, customKey)) {
        return customFields[customKey] as FieldValue
      }
    }
  }

  return undefined
}

const findRecordById = (
  value: unknown,
  id: string,
  depth = 0,
  seen = new WeakSet<object>(),
): RecordLike | null => {
  if (depth > 6 || value === null || typeof value !== 'object') return null

  if (seen.has(value)) return null
  seen.add(value)

  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findRecordById(item, id, depth + 1, seen)
      if (found) return found
    }
    return null
  }

  const record = value as RecordLike
  if (String(record.id ?? '') === id) {
    return record
  }

  const prioritizedKeys = ['data', 'tasks', 'entries', 'items', 'task']
  for (const key of prioritizedKeys) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) continue
    const found = findRecordById(record[key], id, depth + 1, seen)
    if (found) return found
  }

  for (const [key, nested] of Object.entries(record)) {
    if (prioritizedKeys.includes(key)) continue
    const found = findRecordById(nested, id, depth + 1, seen)
    if (found) return found
  }

  return null
}

export const getCurrentFieldHistoryValue = (
  queryClient: QueryClient,
  operation: FieldHistoryOperation,
): FieldValue | undefined => {
  const queries = queryClient.getQueryCache().findAll()
  const moduleRoot = operation.moduleType.toLowerCase().replace(/_/g, '-')

  const sortedQueries = [...queries].sort((left, right) => {
    const leftKey = String(left.queryKey[0] ?? '')
    const rightKey = String(right.queryKey[0] ?? '')
    const leftScore = leftKey.includes(moduleRoot) ? 0 : 1
    const rightScore = rightKey.includes(moduleRoot) ? 0 : 1
    return leftScore - rightScore
  })

  for (const query of sortedQueries) {
    const found = findRecordById(query.state.data, operation.moduleId)
    if (!found) continue

    const value = extractFieldHistoryValue(
      found,
      operation.fieldName,
      operation.fieldId,
    )
    if (value !== undefined) {
      return value
    }
  }

  return undefined
}
